import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileArchive, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
const ACCEPTED_TYPES = {
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

export function UploadDropzone({ onFileSelect, disabled = false, className }) {
  const [error, setError] = useState(null);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors.some(e => e.code === 'file-too-large')) {
          setError('File size must be less than 1GB');
        } else if (rejection.errors.some(e => e.code === 'file-invalid-type')) {
          setError('Only ZIP files are allowed');
        } else {
          setError('Invalid file. Please select a ZIP file under 1GB.');
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled,
  });

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={cn('w-full', className)}>
      <Card
        {...getRootProps()}
        className={cn('border-2 border-dashed transition-colors cursor-pointer', {
          'border-primary bg-primary/5': isDragActive && !isDragReject,
          'border-destructive bg-destructive/5': isDragReject,
          'border-muted-foreground/25 hover:border-muted-foreground/50': !isDragActive && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        })}>
        <CardContent className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <input {...getInputProps()} />

          <div className="mb-4">
            {isDragReject ? (
              <AlertCircle className="h-12 w-12 text-destructive" />
            ) : (
              <div className="relative">
                <FileArchive className="h-6 w-6 text-primary absolute -bottom-1 -right-1" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold">
              {isDragActive
                ? isDragReject
                  ? 'Invalid file type'
                  : 'Drop your ZIP file here'
                : 'Upload ZIP File'}
            </h3>

            <p className="text-sm text-muted-foreground">
              {isDragActive
                ? isDragReject
                  ? 'Only ZIP files are accepted'
                  : 'Release to upload'
                : 'Drag and drop a ZIP file here, or click to browse'}
            </p>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>Maximum file size: {formatFileSize(MAX_FILE_SIZE)}</p>
              <p>Supported format: ZIP (.zip)</p>
            </div>
          </div>

          {!isDragActive && (
            <Button variant="outline" className="mt-4" disabled={disabled}>
              <Upload className="h-4 w-4 mr-2" />
              Choose File
            </Button>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
